// Typed transforms on the selection: M = move by, Q = rotate by, B = save to library.
// A box over the top-left of the drawing, like the draw-by-numbers panel. Enter applies,
// Esc closes. Lengths take the drawing's units (3000, 3m, 1200+340).
import { useEffect, useRef, useState } from "preact/hooks";
import type { AppApi } from "../appApi";
import type { SelectionPrompt, ToolManager } from "../tools/toolManager";
import { parseScalar } from "../core/units";
import { categoryChoices, makeLibraryItem } from "../library/library";
import { CategoryPicker } from "./categoryPicker";
import { loadLocalItems } from "../library/store";
import { saveToLibrary, usedCategories } from "../library/save";
import { toasts } from "./toasts";
import { LIBRARY_CHANGED } from "./libraryPanel";

export function SelectionPromptPanel({ api, tools, kind }: { api: AppApi; tools: ToolManager; kind: SelectionPrompt }) {
  const doc = api.store.doc;
  const first = useRef<HTMLInputElement>(null);
  const [a, setA] = useState(kind === "rotate" ? "90" : "0");
  const [b, setB] = useState("0");
  const [copy, setCopy] = useState(false);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Pumps");
  const [where, setWhere] = useState<"account" | "local">("local");
  const [hasAccount, setHasAccount] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    first.current?.focus();
    first.current?.select();
    if (kind !== "save") return;
    void (async () => {
      const eden = await import("../eden");
      const cfg = await eden.loadEdenConfig();
      setHasAccount(!!cfg);
      setWhere(cfg ? "account" : "local");
    })();
  }, [kind]);

  // 3.30.0: a real list (the standard plant and equipment categories and the ones in use), or a new one
  const categories = categoryChoices(usedCategories(loadLocalItems()));

  const close = () => tools.closePrompt();

  const apply = async () => {
    setProblem(null);
    if (kind === "move") {
      const dx = parseScalar(a, doc.units);
      const dy = parseScalar(b, doc.units);
      if (dx === null || dy === null) return setProblem("Type a distance in both boxes (0 for no move).");
      if (!tools.moveSelectionBy(dx, dy, copy)) return setProblem("Nothing moved: the distances are 0, or the selection is locked.");
      toasts.push("success", copy ? "Copy moved." : "Moved.");
      return close();
    }
    if (kind === "rotate") {
      const deg = Number.parseFloat(a);
      if (!Number.isFinite(deg) || deg === 0) return setProblem("Type an angle in degrees, not 0.");
      if (!tools.rotateSelectionBy(deg)) return setProblem("Nothing turned: the selection is locked or empty.");
      toasts.push("success", `Rotated ${deg}°.`);
      return close();
    }
    // save to library
    if (!category.trim()) return setProblem("Choose a category, or type the name of a new one.");
    const made = makeLibraryItem(doc, tools.selection.all, name, category, crypto.randomUUID(), new Date().toISOString());
    if ("error" in made) return setProblem(made.error);
    setBusy(true);
    const why = await saveToLibrary(made.item, where);
    setBusy(false);
    if (why) return setProblem(why);
    toasts.push("success", where === "account" ? `"${made.item.name}" saved to the account library.` : `"${made.item.name}" saved on this computer.`);
    window.dispatchEvent(new Event(LIBRARY_CHANGED));
    close();
  };

  const keys = (e: KeyboardEvent) => {
    e.stopPropagation(); // typing here is never a canvas shortcut
    if (e.key === "Enter") {
      e.preventDefault();
      void apply();
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  const title = kind === "move" ? "Move by" : kind === "rotate" ? "Rotate by" : "Save to library";
  const n = tools.selection.size;

  return (
    <div class="numpanel selprompt" role="dialog" aria-label={title} onKeyDown={keys}>
      <span class="numpanel-title">
        {title} <span class="muted">· {n} selected</span>
      </span>

      {kind === "move" && (
        <>
          <label class="numpanel-field">
            <span>Across (X)</span>
            <input id="prompt-move-x" ref={first} value={a} onInput={(e) => setA((e.target as HTMLInputElement).value)} />
          </label>
          <label class="numpanel-field">
            <span>Down (Y)</span>
            <input id="prompt-move-y" value={b} onInput={(e) => setB((e.target as HTMLInputElement).value)} />
          </label>
          <label class="numpanel-check">
            <input id="prompt-move-copy" type="checkbox" checked={copy} onChange={() => setCopy(!copy)} /> Move a copy
          </label>
        </>
      )}

      {kind === "rotate" && (
        <>
          <label class="numpanel-field">
            <span>Angle °</span>
            <input id="prompt-rotate" ref={first} value={a} onInput={(e) => setA((e.target as HTMLInputElement).value)} />
          </label>
          {["90", "-90", "180", "45"].map((v) => (
            <button key={v} class="numpanel-quick" onClick={() => setA(v)} title={Number(v) > 0 ? "clockwise" : "anticlockwise"}>
              {v}°
            </button>
          ))}
        </>
      )}

      {kind === "save" && (
        <>
          <label class="numpanel-field wide">
            <span>Name</span>
            <input id="prompt-save-name" ref={first} value={name} placeholder="e.g. Pump 1.5 kW" onInput={(e) => setName((e.target as HTMLInputElement).value)} />
          </label>
          <label class="numpanel-field">
            <span>Category</span>
            <CategoryPicker id="prompt-save-cat" value={category} choices={categories} onChange={setCategory} />
          </label>
          <label class="numpanel-field">
            <span>Keep in</span>
            <select id="prompt-save-where" value={where} onChange={(e) => setWhere((e.target as HTMLSelectElement).value as "account" | "local")}>
              {hasAccount && <option value="account">Account library (Eden)</option>}
              <option value="local">This computer</option>
            </select>
          </label>
        </>
      )}

      <button class="primary" disabled={busy} onClick={() => void apply()}>
        {kind === "save" ? (busy ? "Saving…" : "Save") : "Apply"}
      </button>
      <button onClick={close}>Cancel</button>
      <small class={problem ? "numpanel-note bad" : "numpanel-note"}>
        {problem ??
          (kind === "move"
            ? `${doc.units} · positive X moves right, positive Y moves down · Enter applies`
            : kind === "rotate"
              ? "About the centre of the selection · positive turns clockwise · Enter applies"
              : "Saved centred on the middle of the selection. Several objects are saved as one group.")}
      </small>
    </div>
  );
}
